import React, { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { ErrorIcon } from '@/assets/icons/error';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import type { TextProps } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useFormatCurrency } from '@/hooks/use-format-currency';

import { ds } from './lib/design.system';

const VARIANT_COPY = {
    cashOut: {
        newPrice: 'bets:new_cash_out_price',
        previousPrice: 'bets:previous_cash_out_price',
        errorTitle: 'bets:cashout_failed',
        errorDescription: 'bets:cashout_failed_description',
        errorByCode: 'error:cashout',
        complete: 'bets:complete_cash_out_plain',
    },
    sellPosition: {
        newPrice: 'bets:new_sell_price',
        previousPrice: 'bets:previous_sell_price',
        errorTitle: 'bets:position_sell_failed',
        errorDescription: 'bets:position_sell_failed_description',
        errorByCode: 'error:sell_position',
        complete: 'bets:complete_position_sell',
    },
} as const;

type CashOutSheetVariant = keyof typeof VARIANT_COPY;
type CashOutCopy = (typeof VARIANT_COPY)[CashOutSheetVariant];

type CashOutSheetsConfig = {
    /** Currency indicator rendered before the amount in price rows and the confirm button.
     *  Pass a sweeps icon for SBK; omit for products where formatCurrency already includes the symbol. */
    currencyNode?: ReactNode;
    variant?: CashOutSheetVariant;
};

const useShowCashOutSheet = () => {
    const { t } = useTranslation('common');
    const { showInfoSheet } = useAlerts();
    return (options: Parameters<typeof showInfoSheet>[0]) =>
        showInfoSheet({
            containerStyle: styles.sheetTopPadding,
            titleVariant: 'headlineMedium',
            secondaryLabel: t('close'),
            ...options,
        });
};

export const useCashOutSheets = (config?: CashOutSheetsConfig) => {
    const { currencyNode, variant = 'cashOut' } = config ?? {};
    const keys = VARIANT_COPY[variant];
    const { t } = useTranslation(['bets', 'common', 'error']);
    const showCashOutSheet = useShowCashOutSheet();
    const failedDescription = t(keys.errorDescription);

    const showPriceChange = ({
        previous,
        next,
        onComplete,
    }: {
        previous: number;
        next: number;
        onComplete: () => void;
    }) =>
        showCashOutSheet({
            footerContent: <SizedBox value={24} />,
            title: t('bets:price_change'),
            description: (
                <PriceChangeRows newAmount={next} previousAmount={previous} currencyNode={currencyNode} keys={keys} />
            ),
            buttonLabel: <CompleteCashOutLabel amount={next} currencyNode={currencyNode} completeKey={keys.complete} />,
            handlePress: onComplete,
        });

    const showCashOutError = ({ code, onRetry }: { code?: string; onRetry: () => void }) =>
        showCashOutSheet({
            icon: <ErrorIcon />,
            footerContent: <SizedBox value={48} />,
            title: t(keys.errorTitle),
            description: code ? t(`${keys.errorByCode}.${code}`, failedDescription) : failedDescription,
            buttonLabel: t('common:retry'),
            handlePress: onRetry,
        });

    return { showPriceChange, showCashOutError };
};

type PriceRowProps = {
    label: string;
    amount: string;
    currencyNode?: ReactNode;
    superseded?: boolean;
};

const PriceRow = ({ label, amount, currencyNode, superseded }: PriceRowProps) => {
    const color: TextProps['color'] = superseded ? 'neutral400' : ds.colorToken.text.primary;
    return (
        <Row height={56} alignItems="center" justifyContent="space-between">
            <Text variant="bodyMedium" color={color}>
                {label}
            </Text>
            <Row alignItems="center" gap="s4">
                {currencyNode ? <Box opacity={superseded ? 0.3 : 1}>{currencyNode}</Box> : null}
                <Text variant="bodyMedium" color={color} style={superseded ? styles.struck : undefined}>
                    {amount}
                </Text>
            </Row>
        </Row>
    );
};

const PriceChangeRows = ({
    newAmount,
    previousAmount,
    currencyNode,
    keys,
}: {
    newAmount: number;
    previousAmount: number;
    currencyNode?: ReactNode;
    keys: CashOutCopy;
}) => {
    const { t } = useTranslation(['bets']);
    const { formatCurrency } = useFormatCurrency();
    return (
        <Column pt="s16">
            <PriceRow label={t(keys.newPrice)} amount={formatCurrency(newAmount)} currencyNode={currencyNode} />
            <Box height={1} backgroundColor={ds.colorToken.background.neutral.subtler} />
            <PriceRow
                label={t(keys.previousPrice)}
                amount={formatCurrency(previousAmount)}
                currencyNode={currencyNode}
                superseded
            />
        </Column>
    );
};

const CompleteCashOutLabel = ({
    amount,
    currencyNode,
    completeKey,
}: {
    amount: number;
    currencyNode?: ReactNode;
    completeKey: CashOutCopy['complete'];
}) => {
    const { t } = useTranslation(['bets']);
    const { formatCurrency } = useFormatCurrency();
    const formattedAmount = formatCurrency(amount);

    if (currencyNode) {
        return (
            <Row alignItems="center" gap="s4">
                <Trans i18nKey="complete_cash_out" ns="bets" values={{ amount: formattedAmount }}>
                    <Text variant="titleMedium2" color="neutral800">
                        Complete
                    </Text>
                    {currencyNode}
                    <Text variant="titleMedium2" color="neutral800">
                        amount cash out
                    </Text>
                </Trans>
            </Row>
        );
    }

    return (
        <Text variant="titleMedium2" color="neutral800">
            {t(completeKey, { amount: formattedAmount })}
        </Text>
    );
};

const styles = StyleSheet.create({
    struck: { textDecorationLine: 'line-through' },
    sheetTopPadding: { paddingTop: 32 },
});
